import { useState, type FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import * as tracking from 'dibs-tracking';
import { SeoLink } from 'dibs-seo/exports/SeoLink';
import { NO_CONTENT_LINK } from 'dibs-elements/exports/SeoLink';
import { QuickViewButton } from 'dibs-search-product-tile/exports/QuickViewButton';
import { SbSharedCreatorDesignsQuickView } from './SbSharedCreatorDesignsQuickView';
import { getSrcsetString } from 'dibs-image-utils/exports/srcSet';
import { FormattedMessage, FormattedNumber } from 'dibs-react-intl';
import classNames from 'classnames';

import styles from './SbSharedCreatorDesignsTile.scss';

import {
    type SbSharedCreatorDesignsTile_creatorDesigns$key,
    type Currency,
} from './__generated__/SbSharedCreatorDesignsTile_creatorDesigns.graphql';

type Props = {
    iconicDesign: SbSharedCreatorDesignsTile_creatorDesigns$key;
    currency: Currency;
    isMobile?: boolean;
};

const trackCreatorDesignClick: () => void = () => {
    tracking.trackEvent({
        category: 'promo interaction',
        action: 'more from iconic design creator clicked',
        label: 'design browse page',
    });
};

export const SbSharedCreatorDesignsTile: FC<Props> = ({
    iconicDesign: iconicDesignRef,
    currency,
    isMobile,
}) => {
    const iconicDesign = useFragment(
        graphql`
            fragment SbSharedCreatorDesignsTile_creatorDesigns on CreatorDesigns {
                displayName
                imageUrl
                browsePageLinkData {
                    isLinkable
                    ...SeoLink_linkData
                }
                quickViewDisplay(uriRef: $uriRef) {
                    ...SbSharedCreatorDesignsQuickView_quickViewDisplay
                    paragraphAttributeNames {
                        averagePrice {
                            convertedAmountList {
                                amount
                                currency
                            }
                        }
                        numAvailable
                    }
                }
            }
        `,
        iconicDesignRef
    );
    const [isQuickViewActive, setIsQuickViewActive] = useState(false);
    const { browsePageLinkData, imageUrl, displayName, quickViewDisplay } = iconicDesign || {};
    const paragraphAttributeNames = quickViewDisplay?.paragraphAttributeNames;
    const designAveragePrice =
        (currency &&
            (paragraphAttributeNames?.averagePrice?.convertedAmountList || []).find(
                convertedAmount => convertedAmount?.currency === currency
            )?.amount) ||
        null;
    const numAvailable = paragraphAttributeNames?.numAvailable;

    if (!imageUrl || !browsePageLinkData?.isLinkable || !displayName) {
        return null;
    }

    return (
        <div className={styles.design}>
            <div className={classNames(styles.tile, { [styles.hidden]: !!isQuickViewActive })}>
                <SeoLink
                    className={styles.link}
                    dataTn="more-designs-link"
                    linkData={browsePageLinkData}
                    onClick={trackCreatorDesignClick}
                    variant={NO_CONTENT_LINK}
                >
                    <div className={styles.imageContainer} data-tn="more-designs-image-container">
                        <img
                            data-tn="more-designs-image"
                            className={styles.image}
                            src={imageUrl}
                            srcSet={getSrcsetString(imageUrl)}
                            alt={displayName || ''}
                        />
                    </div>
                    <div className={styles.designName}>
                        <span className={styles.creatorDesignDisplayName}>{displayName}</span>
                        <QuickViewButton
                            hideDivider
                            openQuickView={e => {
                                if (e) {
                                    e.preventDefault();
                                }
                                setIsQuickViewActive(true);
                            }}
                            classname={styles.icon}
                        />
                    </div>
                </SeoLink>
                <div className={styles.spacer} />
                <div className={styles.pageInfoDiv}>
                    <div className={styles.pageInfo}>
                        {designAveragePrice && (
                            <div className={styles.pageInfoDetail}>
                                <div className={styles.pageInfoDetailLabel}>
                                    <FormattedMessage
                                        id="abf.SbSharedCreatorDesignsQuickView.avgPrice"
                                        defaultMessage="Average Price"
                                    />
                                </div>
                                <div className={styles.pageInfoDetailValue}>
                                    <FormattedNumber
                                        value={designAveragePrice}
                                        style="currency"
                                        currency={currency}
                                        minimumFractionDigits={0}
                                        maximumFractionDigits={0}
                                    />
                                </div>
                            </div>
                        )}
                        <div className={styles.pageInfoDetail}>
                            <div className={styles.pageInfoDetailLabel}>
                                <FormattedMessage
                                    id="abf.SbSharedCreatorDesignsQuickView.numAvailable"
                                    defaultMessage="Number Available"
                                />
                            </div>
                            <div className={styles.pageInfoDetailValue}>{numAvailable}</div>
                        </div>
                    </div>
                </div>
            </div>
            <SbSharedCreatorDesignsQuickView
                quickViewDisplay={quickViewDisplay}
                onClose={() => setIsQuickViewActive(false)}
                isVisible={isQuickViewActive}
                isMobile={isMobile}
            />
        </div>
    );
};
